import { CQAppConstant } from "app-constants";
import { IComponentFailure } from "../interfaces/ITask.interface";


/**
 * Capitalizes the first letter of a given string.
 *
 * @param {string} text - The input string to capitalize.
 * @returns {string} The string with its first character in uppercase.
*/
export function capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Sanitizes error objects by converting non-primitive values to strings
 * and filtering out excluded keys. This ensures the DataTable can render all values.
 *
 * @param errors - Array of error objects that may contain nested objects or arrays
 * @returns Sanitized array where all values are primitives (strings, numbers, booleans)
 */
export function sanitizeErrorObjects(errors: Array<Record<string, any>>): Array<Record<string, any>> {
    const MAX_STRING_LENGTH = 500;
    
    return errors.map(error => {
        const sanitized: Record<string, any> = {};
        for (const [key, value] of Object.entries(error)) {
            if (CQAppConstant.ERROR_TABLE_EXCLUDED_KEYS.has(key)) {
                continue;
            }
            if (value === null || value === undefined) {
                sanitized[key] = '';
            } else if (typeof value === 'object') {
                try {
                    let stringified = JSON.stringify(value);
                    if (stringified.length > MAX_STRING_LENGTH) {
                        stringified = stringified.substring(0, MAX_STRING_LENGTH) + '...';
                    }
                    sanitized[key] = stringified;
                } catch (e) {
                    sanitized[key] = '[Complex Object]';
                }
            } else {
                sanitized[key] = value;
            }
        }
        return sanitized;
    });
}

/**
 * Collects the union of displayable keys across error rows, excluding
 * internal flags (changed/created/deleted/success).
 */
export function getErrorTableKeys(errors: Array<Record<string, any>>): string[] {
    return Array.from(
        new Set(errors.flatMap(obj => Object.keys(obj).filter(key => !CQAppConstant.ERROR_TABLE_EXCLUDED_KEYS.has(key))))
    );
}

/**
 * Parses a raw task error message into a renderable format.
 *
 * @param errMessage - The raw error value from the failed task. May be a plain string,
 *                     a JSON-stringified object or array, an actual object/array, or undefined.
 * @returns An `IComponentFailure[]` for Salesforce deployment errors (rendered as a DataTable),
 *          a plain `string` for anonymous Apex failures or text errors,
 *          or an empty string for unrecognised object errors and missing messages.
 *
 * @remarks Strips trailing "..." appended by the backend for truncated messages.
 */
export function parseErrorMessage(errMessage: unknown): IComponentFailure[] | string {
    if (errMessage === null || errMessage === undefined || errMessage === '') return '';

    if (Array.isArray(errMessage)) {
        return errMessage as IComponentFailure[];
    }
    if (typeof errMessage === 'object') {
        return '';
    }

    let cleanedMessage = String(errMessage);
    if (cleanedMessage.endsWith('...')) {
        cleanedMessage = cleanedMessage.slice(0, -3);
    }

    try {
        const parsed = JSON.parse(cleanedMessage);
        if (Array.isArray(parsed)) {
            return parsed as IComponentFailure[];
        }
        if (parsed !== null && typeof parsed === 'object') {
            // Anonymous Apex failure: extract the human-readable exception message from errDetails
            const exceptionMessage = parsed?.errDetails?.exceptionMessage;
            if (exceptionMessage) {
                return String(exceptionMessage);
            }
            // Any other object error: fall through to show generic error message
            return '';
        }
        return String(parsed);
    } catch (e) {
        return cleanedMessage;
    }
}